import { useState, useEffect, useCallback, useId, useMemo, useRef } from "react";
import type { RealtimeChannel, RealtimePostgresChangesPayload, SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { createMirrorAuthSessionScope, type MirrorAuthLease } from "@/lib/mirrorAuthSession";
import { hasNotExpired, isActiveStatus, isPremiumPlan, isProPlan, type Plan, type Status } from "@/lib/subscriptionHelpers";

// Read-only subset of the existing table, inspected on 2026-10-13. The global
// generated schema omits this contract; this cast changes no grants or requests.
type SubscriptionRow = {
  user_id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  canceled_at: string | null;
};
type SubscriptionDatabase = Pick<Database, "__InternalSupabase"> & { public: {
  Tables: { subscriptions: {
    Row: SubscriptionRow; Insert: never; Update: never; Relationships: [];
  } };
  Views: Record<never, never>; Functions: Record<never, never>;
  Enums: Record<never, never>; CompositeTypes: Record<never, never>;
} };
const subscriptionsClient = supabase as unknown as SupabaseClient<SubscriptionDatabase>;
type Values = { plan: Plan; status: Status; currentPeriodEnd: string | null; canceledAt: string | null };
type Actor = Pick<MirrorAuthLease, "ownerId" | "authSessionId" | "generation">;
type Snapshot = { actor: Actor; values: Values };
const FREE: Values = { plan: "free", status: "active", currentPeriodEnd: null, canceledAt: null };
const valuesOf = (row: Pick<SubscriptionRow, "plan" | "status" | "current_period_end" | "canceled_at"> | null): Values => row ? {
  plan: (row.plan as Plan) || "free",
  status: (row.status as Status) || "active",
  currentPeriodEnd: typeof row.current_period_end === "string" ? row.current_period_end : null,
  canceledAt: typeof row.canceled_at === "string" ? row.canceled_at : null,
} : FREE;

interface SubscriptionState extends Values {
  isPremium: boolean; isPro: boolean; isUltra: boolean; isActive: boolean;
  isCanceledButActive: boolean; isLoading: boolean; refresh: () => void;
}

/** Client display cancellation only; server entitlements and RLS remain authoritative. */
export function useSubscription(): SubscriptionState {
  const scope = useMemo(createMirrorAuthSessionScope, []);
  const instance = useId();
  const reloadRef = useRef<() => void>(() => {});
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refresh = useCallback(() => reloadRef.current(), []);

  useEffect(() => {
    let alive = true, run = 0, realtimeRevision = 0;
    let activeRead: { controller: AbortController } | null = null;
    let recoveryPending = false, channel: RealtimeChannel | null = null;
    let scheduled: ReturnType<typeof setTimeout> | null = null;
    const removed = new WeakSet<RealtimeChannel>();
    const remove = (owned: RealtimeChannel | null) => {
      if (!owned || removed.has(owned)) return;
      removed.add(owned);
      try {
        void Promise.resolve(supabase.removeChannel(owned)).catch(() => {
          console.error("[useSubscription] channel cleanup failed");
        });
      } catch { console.error("[useSubscription] channel cleanup failed"); }
    };
    const cancelWork = () => {
      run++; realtimeRevision++;
      if (scheduled !== null) clearTimeout(scheduled);
      scheduled = null;
      activeRead?.controller.abort(); activeRead = null; recoveryPending = false;
      const previous = channel; channel = null; remove(previous);
    };
    const current = (lease: MirrorAuthLease, request: number) =>
      alive && run === request && scope.isCurrent(lease);
    const loseIdentity = () => {
      scope.invalidate(); cancelWork();
      if (alive) { setSnapshot(null); setIsLoading(false); }
    };
    // A read verifies its original actor. It cannot adopt a different session.
    const checkSession = async (lease: MirrorAuthLease, request: number) => {
      const result = await supabase.auth.getSession();
      if (!current(lease, request)) return false;
      if (result.error || !scope.matchesSession(result.data.session, lease)) {
        loseIdentity(); return false;
      }
      return true;
    };
    const publish = (lease: MirrorAuthLease, request: number, values: Values) => {
      if (!current(lease, request)) return;
      setSnapshot({ actor: { ownerId: lease.ownerId, authSessionId: lease.authSessionId, generation: lease.generation }, values });
    };
    const schedule = () => {
      cancelWork();
      const lease = scope.capture(), request = run;
      if (!alive) return;
      if (!lease) { setSnapshot(null); setIsLoading(false); return; }
      setIsLoading(true);
      // Auth notifications stay synchronous. Reads start in a later task after
      // the SDK's notification/lock processing, and an obsolete task is canceled.
      scheduled = setTimeout(() => {
        scheduled = null;
        if (current(lease, request)) void load(lease, request);
      }, 0);
    };
    const recover = () => {
      const lease = scope.capture(), request = run;
      if (!lease || !current(lease, request)) return;
      // A pending task will read the latest bearer/row. A dispatched read may
      // predate recovery, so keep it and coalesce notifications into one followup.
      if (scheduled !== null) return;
      if (activeRead) { recoveryPending = true; return; }
      scheduled = setTimeout(() => {
        scheduled = null;
        if (current(lease, request)) void load(lease, request);
      }, 0);
    };
    const realtime = async (lease: MirrorAuthLease, request: number, payload: RealtimePostgresChangesPayload<SubscriptionRow>) => {
      if (!current(lease, request)) return;
      const next = payload.new as Partial<SubscriptionRow>;
      const previous = payload.old as Partial<SubscriptionRow>;
      // DELETE payloads can contain only the primary key and are not RLS-filtered.
      // Never use them as subscription values; reread only the current owned row.
      if (payload.eventType === "DELETE") {
        if (previous.user_id && previous.user_id !== lease.ownerId) return;
        const event = ++realtimeRevision;
        if (await checkSession(lease, request) && event === realtimeRevision) recover();
        return;
      }
      if ((payload.eventType !== "INSERT" && payload.eventType !== "UPDATE") ||
        next.user_id !== lease.ownerId || typeof next.plan !== "string" || typeof next.status !== "string") return;
      const event = ++realtimeRevision;
      if (!await checkSession(lease, request) || event !== realtimeRevision) return;
      publish(lease, request, valuesOf({ plan: next.plan, status: next.status,
        current_period_end: typeof next.current_period_end === "string" ? next.current_period_end : null,
        canceled_at: typeof next.canceled_at === "string" ? next.canceled_at : null }));
      if (current(lease, request)) setIsLoading(false);
    };
    const load = async (lease: MirrorAuthLease, request: number) => {
      const read = { controller: new AbortController() };
      activeRead = read;
      try {
        if (!await checkSession(lease, request)) return;
        const bearer = scope.capture();
        if (!bearer || !current(lease, request)) return;
        const revision = realtimeRevision;
        const { data, error } = await subscriptionsClient.from("subscriptions")
          .select("plan, status, current_period_end, canceled_at")
          .eq("user_id", lease.ownerId)
          .setHeader("Authorization", "Bearer " + bearer.accessToken)
          .abortSignal(read.controller.signal)
          .maybeSingle();
        if (!current(lease, request) || !await checkSession(lease, request)) return;
        if (error) console.error("[useSubscription] query failed");
        // A newer owned Realtime event wins over this older HTTP snapshot,
        // including while its session verification is still pending.
        else if (revision === realtimeRevision) publish(lease, request, valuesOf(data));
        if (!current(lease, request)) return;
        setIsLoading(false);
        if (channel) return;
        const owned = supabase.channel("sub-" + lease.ownerId + "-" + instance + "-" + lease.generation + "-" + request);
        if (!current(lease, request)) { remove(owned); return; }
        channel = owned;
        owned.on<SubscriptionRow>("postgres_changes", {
          event: "*", schema: "public", table: "subscriptions", filter: "user_id=eq." + lease.ownerId,
        }, payload => {
          void realtime(lease, request, payload).catch(() => {
            if (current(lease, request)) console.error("[useSubscription] realtime session unavailable");
          });
        });
        if (!current(lease, request)) { remove(owned); return; }
        owned.subscribe();
        if (!current(lease, request)) remove(owned);
      } catch {
        if (current(lease, request)) {
          console.error("[useSubscription] load failed"); setIsLoading(false);
        }
      } finally {
        // An old canceled read must not clear or schedule work for a new actor.
        if (activeRead === read) {
          activeRead = null;
          if (current(lease, request) && recoveryPending) {
            recoveryPending = false; recover();
          }
        }
      }
    };
    reloadRef.current = schedule;
    const bootstrapRevision = scope.revision();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!alive) return;
      if (event === "SIGNED_OUT") { loseIdentity(); return; }
      const { identity, changed } = scope.observeSession(session);
      if (!identity) { loseIdentity(); return; }
      if (changed) { setSnapshot(null); schedule(); }
      else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") recover();
      // Same owner/SID recovery rereads a possibly missed row without erasing
      // the display, aborting its pending lease or replacing its owned channel.
    });
    void supabase.auth.getSession().then(result => {
      if (!alive || scope.revision() !== bootstrapRevision) return;
      if (result.error) { loseIdentity(); return; }
      const { identity } = scope.observeSession(result.data.session);
      if (identity) schedule(); else loseIdentity();
    }).catch(() => {
      if (alive && scope.revision() === bootstrapRevision) loseIdentity();
    });
    return () => {
      alive = false; reloadRef.current = () => {};
      scope.invalidate(); cancelWork(); subscription.unsubscribe();
    };
  }, [scope, instance]);

  const lease = scope.capture();
  const visible = snapshot && lease && snapshot.actor.ownerId === lease.ownerId &&
    snapshot.actor.authSessionId === lease.authSessionId && snapshot.actor.generation === lease.generation;
  const { plan, status, currentPeriodEnd, canceledAt } = visible ? snapshot.values : FREE;
  // Existing billing rules are unchanged: paid plan + a future period. Canceled
  // periods retain access until expiry; status still controls only isActive.
  const notExpired = hasNotExpired(currentPeriodEnd), paid = isPremiumPlan(plan);
  return { plan, status, currentPeriodEnd, canceledAt,
    isPremium: paid && notExpired, isPro: isProPlan(plan) && notExpired,
    isUltra: plan === "ultra" && notExpired, isActive: isActiveStatus(status),
    isCanceledButActive: paid && status === "canceled" && notExpired,
    isLoading, refresh };
}
